/**
 * E1 · অ্যাক্টিভিটি তালিকা — `/activity`
 *
 * Transcribed from `design/Oprappo P0 Screens.dc.html`, frame E1 (360 × 680).
 *
 * Frame note: "Five transaction types in one list — service, grocery, bill, recharge,
 * ride. Three tabs, not one per domain: that is the trap this screen exists to avoid."
 *
 * Staleness is per row. When a row carries a stale label it is drawn on that row, in
 * place of nothing else, exactly as `P0 States § ৭ · সিঙ্ক ত্রুটি` draws it. There is no
 * dismissible banner in this screen, because a dismissed banner is forgotten and a
 * per-row timestamp is not (docs/05 § activity).
 *
 * WIRED. `GET /v1/activity?tab=…` supplies the title, the three tabs and every row,
 * including `meta_bn`, the badge word and — during a sync failure — `stale_label_bn`.
 * Nothing on this screen is composed here: `১১:৪০-এর তথ্য` is Bangla prose around Bangla
 * numerals and `states/INTEGRATION.md § 5` calls building it on the client a defect.
 *
 * DEVIATIONS reported rather than resolved:
 *  · The tab rail is transcribed, not § 03's `Tabs`. The component is `px-[3px] py-[11px]`
 *    with a 20px gutter; E1 draws `padding:0 3px 11px` with a 22px gutter, so using the
 *    component would push the whole list down 11px.
 *  · Two of the five row glyphs are reduced forms of § 01 icons: the market bag is drawn
 *    without its handle and the ride without its wheels. Transcribed as drawn.
 *  · The stale chip has no tone in § 03's `Badge` (success / pending / danger only); it
 *    is a `#8B9795` border with muted text. Transcribed from the States frame.
 */
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Icon, TabBar } from '@oprappo/ui';
import { formatTaka } from '@oprappo/contracts';
import type {
  ActivityGlyph,
  ActivityListResponse,
  ActivityListRow,
  ActivityTabId,
} from '@oprappo/contracts';
import { Frame, SAND_FRAME } from '../offering/chrome.js';
import { SurfaceStates } from '../../states/boundary.js';
import { useResource } from '../../data/resource.js';
import { fetchActivities } from './api.js';

/**
 * The five row glyphs, 19px, `2px`, teal. `bill` and `recharge` are § 01's icons path
 * for path, so the component is used; the other three are drawn only here.
 */
function RowGlyph({ glyph }: { glyph: ActivityGlyph }): JSX.Element {
  if (glyph === 'bill') return <Icon name="bill" size={19} />;
  if (glyph === 'recharge') return <Icon name="recharge" size={19} />;
  return (
    <svg width={19} height={19} viewBox="0 0 24 24" fill="none" aria-hidden>
      {glyph === 'service' ? (
        <path d="M6 3.5v6a6 6 0 0012 0v-6" stroke="#0E3A45" strokeWidth={2} strokeLinecap="round" />
      ) : glyph === 'market' ? (
        // § 01's market icon adds `M9 7V5a3 3 0 016 0v2`; this row draws the bag alone.
        <path d="M3.5 7h17l-1.5 13h-14z" stroke="#0E3A45" strokeWidth={2} strokeLinejoin="round" />
      ) : (
        // § 01's transport icon adds two wheels; this row draws the body alone.
        <path
          d="M3 15h18M6 15V9l3-3h6l3 3v6"
          stroke="#0E3A45"
          strokeWidth={2}
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

function Row({ row, last, onOpen }: { row: ActivityListRow; last: boolean; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={
        'w-full py-[14px] flex gap-[12px] items-center text-left bg-transparent px-0 rounded-none' +
        (last ? '' : ' border-b border-border')
      }
    >
      <span className="w-[36px] h-[36px] box-content border border-border bg-white flex items-center justify-center flex-none">
        <RowGlyph glyph={row.glyph} />
      </span>
      <span className="flex-1">
        <span className="block font-anek font-medium text-[15px] leading-[1.75] text-ink">
          {row.label_bn}
        </span>
        <span className="block font-anek font-normal text-[13.5px] leading-[1.75] text-muted mt-[1px]">
          {row.meta_bn}
        </span>
      </span>
      <span className="text-right flex-none">
        {row.amount_minor === null ? null : (
          <span className="block font-manrope font-semibold text-[15px] leading-[1] text-ink tabular-nums">
            {formatTaka(row.amount_minor)}
          </span>
        )}
        {row.status_bn !== null && row.status_tone !== null ? (
          <span className="flex mt-[5px]">
            <Badge labelBn={row.status_bn} tone={row.status_tone} dense className="w-full" />
          </span>
        ) : null}
        {row.stale_label_bn === null ? null : (
          // `P0 States § ৭`: `border:1px solid #8B9795; padding:2px 7px;
          // font:500 13px/1.75 'Anek Bangla'; color:#5B6B69` — one label per row.
          <span className="flex mt-[5px]">
            <span className="w-full border border-grey-border px-[7px] py-[2px] font-anek font-medium text-[13px] leading-[1.75] text-muted whitespace-nowrap">
              {row.stale_label_bn}
            </span>
          </span>
        )}
      </span>
    </button>
  );
}

/**
 * THE SCREEN IS A PURE FUNCTION OF THE PAYLOAD.
 *
 * The view takes what `GET /v1/activity` returned and renders it; the container below
 * does the loading. The split is not ceremony: it is what lets the fidelity tests render
 * this screen from `fixtures/api/activity.e1-list.json` — the same file the API's own
 * suite asserts its response against — so the chain the gate depends on is checked end
 * to end: drawn frame == fixture == API output == this markup.
 *
 * `null` means "nothing has arrived yet". The view renders its chrome and no rows rather
 * than substituting design data, because a screen that quietly shows fixtures is a screen
 * that looks right in review and lies in production.
 */
export function E1ActivityView({
  data,
  tabId,
  onTab,
}: {
  data: ActivityListResponse | null;
  tabId: ActivityTabId;
  onTab: (id: ActivityTabId) => void;
}): JSX.Element {
  const navigate = useNavigate();
  const rows = data?.items ?? [];

  return (
      <Frame className={SAND_FRAME}>
        <div className="bg-white px-[16px] pt-[16px] border-b border-border">
          <div className="font-anek font-semibold text-[22px] leading-[1.25] text-ink mb-[14px]">
            {data?.title_bn ?? ''}
          </div>
          <div role="tablist" className="flex">
            {(data?.tabs ?? []).map((t, i) => {
              const active = t.id === tabId;
              return (
                <button
                  key={t.id}
                  role="tab"
                  type="button"
                  aria-selected={active}
                  onClick={() => onTab(t.id)}
                  className={
                    // `pt-0`: Tailwind's preflight is off in this app, so a <button> keeps the UA's
                    // own `padding:1px 6px` on any side no utility sets. The frame draws
                    // `padding:0 3px 11px`.
                    'px-[3px] pt-0 pb-[11px] bg-transparent border-0 rounded-none font-anek text-[15px] leading-[1.75]' +
                    (i < (data?.tabs.length ?? 0) - 1 ? ' mr-[22px]' : '') +
                    (active
                      ? ' font-semibold text-teal border-b-2 border-teal mb-[-1px]'
                      : ' font-medium text-muted')
                  }
                >
                  {t.label_bn}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 px-[16px] oprappo-scroll">
          {rows.map((r, i) => (
            <Row
              key={r.id}
              row={r}
              last={i === rows.length - 1}
              onOpen={() => navigate(`/activity/${r.id}`)}
            />
          ))}
        </div>

        <TabBar
          activeKey="activity"
          onChange={(key) => {
            if (key === 'home') navigate('/');
            else if (key === 'account') navigate('/account');
            else if (key === 'explore') navigate('/explore');
            else if (key === 'ai') navigate('/ai');
          }}
        />
      </Frame>
  );
}

export function E1Activity(): JSX.Element {
  const [tabId, setTabId] = useState<ActivityTabId>('all');

  // THE TAB IS A QUERY, NOT A FILTER. The server decides which status keys count as
  // সম্পন্ন (`activity/copy.ts § DONE_STATUS_KEYS`), so a status nobody has thought
  // about yet lands under চলমান, where the customer sees it. A client-side
  // `status_key === 'done'` put that decision in a transcription and would hide the row.
  const load = useCallback((signal: AbortSignal) => fetchActivities(tabId, signal), [tabId]);
  const { data, error } = useResource(load, tabId);

  return (
    <SurfaceStates surface="activity" error={error ?? undefined}>
      <E1ActivityView data={data} tabId={tabId} onTab={setTabId} />
    </SurfaceStates>
  );
}
